// frontend/src/components/UserList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import api from '../api';

const ROLES = ['admin', 'chef_chantier', 'ouvrier'];

const ROLE_BADGE = {
  admin:         { bg: 'danger',  label: 'Admin' },
  chef_chantier: { bg: 'warning', label: 'Chef chantier' },
  ouvrier:       { bg: 'secondary', label: 'Ouvrier' },
};

const EMPTY_FORM = {
  username: '', email: '', password: '', role: 'ouvrier', active: true
};

function UserList({ currentUser }) {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editUser, setEditUser]       = useState(null);
  const [userToToggle, setUserToToggle] = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [submitting, setSubmitting]   = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreateModal = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setForm({
      username: user.username,
      email:    user.email || '',
      password: '', // ← vide, on ne pré-remplit jamais un mot de passe
      role:     user.role,
      active:   user.active
    });
    setError(null);
    setShowModal(true);
  };

  const handleField = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // En création, le mot de passe est obligatoire
    if (!editUser && !form.password) {
      setError('Le mot de passe est requis pour la création');
      setSubmitting(false);
      return;
    }

    try {
      const payload = { ...form };
      // En édition, n'envoie pas le mot de passe si vide
      if (editUser && !payload.password) delete payload.password;

      if (editUser) {
        await api.put(`/users/${editUser.id}`, payload);
      } else {
        await api.post('/users', payload);
      }
      setShowModal(false);
      await fetchUsers();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async () => {
    try {
      await api.put(`/users/${userToToggle.id}/toggle`);
      setShowConfirm(false);
      setUserToToggle(null);
      await fetchUsers();
    } catch (err) {
      setShowConfirm(false);
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des utilisateurs...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <h4 className="mb-0">Utilisateurs ({users.length})</h4>
        <Button variant="primary" onClick={openCreateModal}>
          + Nouvel Utilisateur
        </Button>
      </div>

      <Table striped bordered hover responsive>
        <thead className="table-dark">
          <tr>
            <th>#</th>
            <th>Nom d'utilisateur</th>
            <th>Email</th>
            <th>Rôle</th>
            <th>Statut</th>
            <th>Dernière connexion</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} className={!user.active ? 'table-secondary' : ''}>
              <td>{user.id}</td>
              <td>
                <strong>{user.username}</strong>
                {user.id === currentUser?.id && (
                  <Badge bg="info" className="ms-2">Vous</Badge>
                )}
              </td>
              <td>{user.email || '-'}</td>
              <td>
                <Badge bg={ROLE_BADGE[user.role]?.bg || 'secondary'}>
                  {ROLE_BADGE[user.role]?.label || user.role}
                </Badge>
              </td>
              <td>
                <Badge bg={user.active ? 'success' : 'danger'}>
                  {user.active ? 'Actif' : 'Inactif'}
                </Badge>
              </td>
              <td>{formatDate(user.last_login)}</td>
              <td>
                <div className="d-flex gap-1">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => openEditModal(user)}
                  >
                    ✏️
                  </Button>
                  <Button
                    size="sm"
                    variant={user.active ? 'outline-danger' : 'outline-success'}
                    disabled={user.id === currentUser?.id}
                    onClick={() => { setUserToToggle(user); setShowConfirm(true); }}
                    title={user.id === currentUser?.id ? 'Impossible de désactiver votre propre compte' : ''}
                  >
                    {user.active ? '🔒' : '🔓'}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Modal création / édition */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editUser ? '✏️ Modifier l\'utilisateur' : '👤 Nouvel Utilisateur'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Nom d'utilisateur *</Form.Label>
              <Form.Control
                type="text"
                name="username"
                required
                value={form.username}
                onChange={handleField}
                autoComplete="off"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={form.email}
                onChange={handleField}
                autoComplete="off"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Mot de passe {editUser ? '(laisser vide pour ne pas changer)' : '*'}
              </Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={form.password}
                onChange={handleField}
                autoComplete="new-password"
                required={!editUser}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Rôle</Form.Label>
              <Form.Select name="role" value={form.role} onChange={handleField}>
                {ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_BADGE[r].label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {editUser && editUser.id !== currentUser?.id && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  name="active"
                  label="Compte actif"
                  checked={form.active}
                  onChange={handleField}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting
                ? (editUser ? 'Mise à jour...' : 'Création...')
                : (editUser ? 'Mettre à jour' : 'Créer')
              }
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal confirmation toggle */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirmer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userToToggle?.active
            ? <>Désactiver le compte de <strong>{userToToggle?.username}</strong> ? Il ne pourra plus se connecter.</>
            : <>Réactiver le compte de <strong>{userToToggle?.username}</strong> ?</>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Annuler
          </Button>
          <Button
            variant={userToToggle?.active ? 'danger' : 'success'}
            onClick={handleToggle}
          >
            {userToToggle?.active ? '🔒 Désactiver' : '🔓 Réactiver'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UserList;